import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Change } from '../../models';
import { Button } from '../Button';
import { EmailAuthBadges } from '../EmailAuthBadges';

export type EmailIntakeHeaders = NonNullable<NonNullable<Change['intake']>['headers']>;

export type EmailHeadersDetailsProps = {
    headers?: EmailIntakeHeaders | null;
    /** When true, only render fail/mismatch badges above the toggle. */
    showFailBadges?: boolean;
    className?: string;
};

const hasHeaderDetails = (headers?: EmailIntakeHeaders | null) => Boolean(
    headers?.auth ||
    headers?.from ||
    headers?.replyTo ||
    headers?.returnPath ||
    headers?.xSpamStatus ||
    headers?.xSpamScore ||
    headers?.raw
);

/**
 * Collapsible forensic email headers (auth summary + selected fields + raw dump).
 */
function EmailHeadersDetails(props: EmailHeadersDetailsProps) {
    const { headers, showFailBadges = false, className = '' } = props;
    const { t } = useTranslation();
    const [showRawHeaders, setShowRawHeaders] = useState(false);
    const [expanded, setExpanded] = useState(false);

    if (!hasHeaderDetails(headers))
        return null;

    return (
        <div className={`w-full space-y-2 ${className}`.trim()}>
            <div className="flex flex-wrap items-center gap-2">
                {showFailBadges && (
                    <EmailAuthBadges headers={headers} failOnly compact />
                )}
                <button
                    type="button"
                    className="inline-flex items-center text-xs font-normal text-gray-600 hover:text-gray-900 dark:text-gray-100 hover:underline bg-transparent border border-gray-200 dark:border-gray-700 rounded px-2 py-1"
                    onClick={() => setExpanded((prev) => !prev)}
                >
                    {expanded
                        ? (t('changes.technical_context_email_headers_hide') as string)
                        : (t('changes.technical_context_email_headers_show') as string)}
                </button>
            </div>
            {expanded && (
                <div className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 space-y-2">
                    <EmailAuthBadges headers={headers} />
                    <dl className="grid grid-cols-1 gap-1 text-xs text-gray-800 dark:text-gray-100">
                        {headers?.from && (
                            <div className="flex gap-2">
                                <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                    {t('changes.technical_context_email_headers_from')}
                                </dt>
                                <dd className="break-all">{headers.from}</dd>
                            </div>
                        )}
                        {headers?.replyTo && (
                            <div className="flex gap-2">
                                <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                    {t('changes.technical_context_email_headers_reply_to')}
                                </dt>
                                <dd className="break-all">{headers.replyTo}</dd>
                            </div>
                        )}
                        {headers?.returnPath && (
                            <div className="flex gap-2">
                                <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                    {t('changes.technical_context_email_headers_return_path')}
                                </dt>
                                <dd className="break-all">{headers.returnPath}</dd>
                            </div>
                        )}
                        {headers?.xSpamStatus && (
                            <div className="flex gap-2">
                                <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                    {t('changes.technical_context_email_headers_x_spam_status')}
                                </dt>
                                <dd className="break-all">{headers.xSpamStatus}</dd>
                            </div>
                        )}
                        {headers?.xSpamScore && (
                            <div className="flex gap-2">
                                <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                    {t('changes.technical_context_email_headers_x_spam_score')}
                                </dt>
                                <dd className="break-all">{headers.xSpamScore}</dd>
                            </div>
                        )}
                    </dl>
                    {headers?.raw && (
                        <div>
                            <Button
                                small
                                color="default"
                                icon={showRawHeaders ? 'up' : 'down'}
                                title={
                                    (showRawHeaders
                                        ? t('changes.technical_context_email_headers_hide')
                                        : t('changes.technical_context_email_headers_show')) as string
                                }
                                onClick={() => setShowRawHeaders(!showRawHeaders)}
                            />
                            {showRawHeaders && (
                                <pre className="mt-2 max-h-80 overflow-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 text-xs font-mono whitespace-pre-wrap break-words text-gray-800 dark:text-gray-100">
                                    {headers.raw}
                                </pre>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default EmailHeadersDetails;
